import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { ABTestCookie, Cookie } from '@as-react/common-types';
import { EventEnum } from '@as-react/common-types';
import { CookieUtil, EventUtil } from '@as-react/common-utils';
import { ShoppingCartFillIcon, ShoppingCartIcon } from '@yonderland/andes-icons';
import { useMediaQueryMatchesOnDesktop, Popover, TextAlpha, Badge, useIsClient, Box, Breakpoint, IconLink, Icon, IconButton, } from '@yonderland/andes-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ADOBE_TARGET_PRESOLDEN } from '../../constants/AdobeTargetConstants';
import { ADD_VOUCHER_PARAM_KEY } from '../../constants/BasketConstants';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { HAS_ACTIVE_SESSION, IS_NEW_SESSION } from '../../constants/StorageKeysConstants';
import { BasketPopoverContainer } from '../../containers/BasketPopoverContainer';
import { useABTest } from '../../hooks/useABTest';
import { useFeatureToggle } from '../../hooks/useFeatureToggle';
import { YonderlandMediatorGroup } from '../../mediator/types/YonderlandMediatorTypes';
import YonderlandMediator from '../../mediator/YonderlandMediator';
import { basketActions } from '../../redux/basket/actions/BasketActions.creators';
import { makeGetBasketPromotionProductsState, makeGetBasketTotalQuantityState, } from '../../redux/basket/selectors/BasketSelector';
import SessionStorageUtil from '../../util/SessionStorageUtil';
import { getQuerystringByName } from '../../util/URLParamUtil';
import { BasketReminderNotification } from '../basket/basket-reminder-notification/BasketReminderNotification';
import { LockedBasketNotificationMessage } from '../notification-message/LockedBasketNotificationMessage';
export const BasketMenuItem = ({ basketOverviewLink, enableBasketReminderNotification, enableBasketReminderNotificationABTest, freeDeliveryMessage, minimumOrderValueMessage, }) => {
    const { t } = useTranslation(I18N_NAMESPACES.navigation);
    const { pageInfo } = useAemConfig();
    const { isConfirmationPage, isPDPPage, isHomePage, isBrandPage, isBrandAsCategoryPage, isListerPage, isCategoryPage, isBasketOverviewPage, } = pageInfo;
    const isCommercialPage = isHomePage || isPDPPage || isBrandPage || isBrandAsCategoryPage || isListerPage || isCategoryPage;
    const isDesktop = useMediaQueryMatchesOnDesktop({ isSsr: true });
    const dispatch = useDispatch();
    const productAmount = useSelector(makeGetBasketTotalQuantityState());
    const activeBasketReminder = useFeatureToggle('active_basket_reminder');
    const noBasketPopup = useFeatureToggle('no_basket_popup_on_click');
    const buttonEl = useRef(null);
    const [isBasketChanged, setIsBasketChanged] = useState(false);
    const promotionProducts = useSelector(makeGetBasketPromotionProductsState());
    const [firstVisit, setFirstVisit] = useState(false);
    const { hasBCookie: showBasketReminderABTest } = useABTest(ABTestCookie.BASKET_REMINDER_NOTIFICATION);
    const isClient = useIsClient();
    const addVoucherParam = isClient ? getQuerystringByName(window.location.search, ADD_VOUCHER_PARAM_KEY) : false;
    const handleBasketChanged = () => {
        setIsBasketChanged(true);
        SessionStorageUtil.setItem(IS_NEW_SESSION, false);
    };
    useEffect(() => {
        EventUtil.listen(EventEnum.BASKET_CHANGED, handleBasketChanged);
        return () => {
            EventUtil.remove(EventEnum.BASKET_CHANGED, handleBasketChanged);
        };
    }, []);
    useEffect(() => {
        var _a;
        if (activeBasketReminder &&
            isCommercialPage &&
            productAmount > 0 &&
            isDesktop &&
            !isBasketChanged &&
            SessionStorageUtil.getJsonItem(IS_NEW_SESSION) &&
            CookieUtil.get(Cookie.IS_BASKET_REMINDER_CALLED) !== 'true') {
            if (!showBasketReminderNotification()) {
                (_a = buttonEl.current) === null || _a === void 0 ? void 0 : _a.click();
            }
            CookieUtil.set(Cookie.IS_BASKET_REMINDER_CALLED, 'true');
        }
    }, [productAmount]);
    useEffect(() => {
        var _a;
        const hasPromotionProduct = promotionProducts.length > 0;
        if (!hasPromotionProduct) {
            return;
        }
        const promotion = (_a = promotionProducts.find(product => !!product.promotion)) === null || _a === void 0 ? void 0 : _a.promotion;
        const minimumBasketSize = promotion === null || promotion === void 0 ? void 0 : promotion.minimumBasketSize;
        !addVoucherParam &&
            YonderlandMediator.publish({
                event: ADOBE_TARGET_PRESOLDEN,
                group: YonderlandMediatorGroup.BASKET,
                data: {
                    origin: 'BasketMenuItemContainer',
                    hasPreSoldenProduct: hasPromotionProduct,
                    isPreSoldenFulfilled: Boolean(hasPromotionProduct && minimumBasketSize && productAmount >= minimumBasketSize),
                    hasMultipleProducts: productAmount > 1,
                },
            });
    }, [promotionProducts]);
    useEffect(() => {
        if (!isConfirmationPage) {
            dispatch(basketActions.createGetAction());
        }
        if (SessionStorageUtil.getJsonItem(IS_NEW_SESSION) === null) {
            SessionStorageUtil.setItem(IS_NEW_SESSION, true);
        }
        if (SessionStorageUtil.getJsonItem(HAS_ACTIVE_SESSION) === null) {
            setFirstVisit(true);
            SessionStorageUtil.setItem(HAS_ACTIVE_SESSION, true);
        }
    }, []);
    const showBasketPopup = isDesktop && !noBasketPopup;
    const handleClick = (e) => {
        e.preventDefault();
        dispatch(basketActions.createGetAction());
    };
    const basketPopoverTitle = () => productAmount
        ? t(productAmount > 1 ? 'basket.popover.title_plural' : 'basket.popover.title', {
            count: productAmount,
        })
        : t('basket.popover.title.default');
    const isFirstVisitAndBasketFilled = () => !isBasketOverviewPage && firstVisit && productAmount > 0 && !isBasketChanged;
    const showBasketReminderNotification = () => isClient &&
        (!enableBasketReminderNotification || (enableBasketReminderNotificationABTest && !showBasketReminderABTest))
        ? false
        : isFirstVisitAndBasketFilled();
    const closenessQualifierMessages = {
        freeDelivery: freeDeliveryMessage,
        minimumOrderValue: minimumOrderValueMessage,
    };
    return (_jsxs("div", { "data-analytics-bannerid": "header-shopping-cart", children: [_jsx(LockedBasketNotificationMessage, {}), showBasketPopup ? (_jsxs(_Fragment, { children: [_jsx(IconButton, { innerRef: buttonEl, testId: "basket-icon", ariaLabel: "basket button", variant: "plain", size: "sm", onClick: handleClick, children: _jsx(BasketIcon, { productAmount: productAmount }) }), _jsxs(Popover, { testId: "basket_popover", placement: "bottom-end", customWidth: "464px", slim: true, targetElement: buttonEl.current, children: [_jsx(Popover.Header, { children: _jsx(Box, { paddingBlockEnd: { base: '2', lg: '6' }, paddingInlineStart: "8", paddingBlockStart: "2", children: _jsx(TextAlpha, { as: "h2", variant: "subheadingM", children: basketPopoverTitle() }) }) }), _jsx(Popover.Body, { children: _jsx(BasketPopoverContainer, { closenessQualifierMessages: closenessQualifierMessages, hrefViewCart: basketOverviewLink, isNewNavigation: true }) })] })] })) : (_jsx(IconLink, { testId: "basket-icon", ariaLabel: "basket button", variant: "plain", size: "sm", href: basketOverviewLink, children: _jsx(BasketIcon, { productAmount: productAmount }) })), showBasketReminderNotification() && _jsx(BasketReminderNotification, { hrefViewCart: basketOverviewLink })] }));
};
const BasketIcon = ({ productAmount }) => (_jsxs(_Fragment, { children: [_jsx(Breakpoint, { from: "lg", children: _jsx(Icon, { size: "sm", title: "basket icon", source: ShoppingCartFillIcon, color: "brand" }) }), _jsx(Breakpoint, { until: "lg", children: _jsx(Icon, { size: "xs", title: "basket icon", source: ShoppingCartIcon, color: "neutral" }) }), productAmount > 0 && (_jsx(Badge, { size: "sm", color: "brandAccent", testId: "basket_counter", children: productAmount }))] }));
